import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { v4 as uuid } from "uuid";
import Button from "../../../components/common/ui/button/Button";
import Label from "../../../components/common/form/Label";
import Input from "../../../components/common/form/input/InputField";
import type { SignerRole, RoutingType } from "../../../types/request";

// Export the same colors used in PdfSignatureBoxDrawer for consistency
export const SIGNER_COLORS = [
  "#3B82F6", // blue
  "#10B981", // green
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#14B8A6", // teal
  "#F97316", // orange
  "#6366F1", // indigo
  "#84CC16", // lime
];

type SignerRolesEditorProps = {
  signers: SignerRole[];
  routing: RoutingType;
  onSignersChange: (signers: SignerRole[]) => void;
  onRoutingChange: (routing: RoutingType) => void;
  maxSigners?: number;
};

/**
 * Generate a key from a label (lowercase, replace spaces with underscores)
 */
export function generateKeyFromLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .substring(0, 50);
}

/**
 * Create a default signer role with auto-generated key/label
 */
export function createDefaultSigner(index: number): SignerRole {
  return {
    id: uuid(),
    key: `signer_${index}`,
    label: `Signer ${index}`,
    signing_order: null,
    required: true,
  };
}

export const SignerRolesEditor: React.FC<SignerRolesEditorProps> = ({
  signers,
  routing,
  onSignersChange,
  onRoutingChange,
  maxSigners = 10,
}) => {
  const handleAddSigner = () => {
    if (signers.length >= maxSigners) return;

    const newIndex = signers.length + 1;
    const newSigner = createDefaultSigner(newIndex);

    // If sequential routing, assign signing order
    if (routing === "sequential") {
      newSigner.signing_order = newIndex;
    }

    onSignersChange([...signers, newSigner]);
  };

  const handleRemoveSigner = (id: string) => {
    if (signers.length <= 1) return; // Must have at least one signer

    const updatedSigners = signers.filter((s) => s.id !== id);

    // Re-assign signing orders if sequential
    if (routing === "sequential") {
      updatedSigners.forEach((signer, idx) => {
        signer.signing_order = idx + 1;
      });
    }

    onSignersChange(updatedSigners);
  };

  const handleLabelChange = (id: string, newLabel: string) => {
    const updatedSigners = signers.map((signer) =>
      signer.id === id
        ? {
            ...signer,
            label: newLabel,
            key: generateKeyFromLabel(newLabel) || signer.key,
          }
        : signer
    );
    onSignersChange(updatedSigners);
  };

  const handleRequiredChange = (id: string, required: boolean) => {
    const updatedSigners = signers.map((signer) =>
      signer.id === id ? { ...signer, required } : signer
    );
    onSignersChange(updatedSigners);
  };

  const handleRoutingChange = (newRouting: RoutingType) => {
    onRoutingChange(newRouting);

    // Update signing orders based on routing
    const updatedSigners = signers.map((signer, idx) => ({
      ...signer,
      signing_order: newRouting === "sequential" ? idx + 1 : null,
    }));
    onSignersChange(updatedSigners);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;

    const updatedSigners = [...signers];
    const temp = updatedSigners[index];
    updatedSigners[index] = updatedSigners[index - 1];
    updatedSigners[index - 1] = temp;

    // Re-assign signing orders
    updatedSigners.forEach((signer, idx) => {
      signer.signing_order = idx + 1;
    });

    onSignersChange(updatedSigners);
  };

  const handleMoveDown = (index: number) => {
    if (index === signers.length - 1) return;

    const updatedSigners = [...signers];
    const temp = updatedSigners[index];
    updatedSigners[index] = updatedSigners[index + 1];
    updatedSigners[index + 1] = temp;

    // Re-assign signing orders
    updatedSigners.forEach((signer, idx) => {
      signer.signing_order = idx + 1;
    });

    onSignersChange(updatedSigners);
  };

  return (
    <div className="space-y-4">
      {/* Routing Selection */}
      <div>
        <Label>Signing Order</Label>
        <div className="mt-2 flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="routing"
              value="parallel"
              checked={routing === "parallel"}
              onChange={() => handleRoutingChange("parallel")}
              className="h-4 w-4 text-brand-500 focus:ring-brand-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Parallel
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              (All signers can sign at once)
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="routing"
              value="sequential"
              checked={routing === "sequential"}
              onChange={() => handleRoutingChange("sequential")}
              className="h-4 w-4 text-brand-500 focus:ring-brand-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Sequential
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              (Signers sign in order)
            </span>
          </label>
        </div>
      </div>

      {/* Signers List */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Signer Roles</Label>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {signers.length} / {maxSigners} signers
          </span>
        </div>

        <div className="space-y-2">
          {signers.map((signer, index) => {
            const color = SIGNER_COLORS[index % SIGNER_COLORS.length];

            return (
              <div
                key={signer.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                {/* Color indicator */}
                <div
                  className="w-4 h-8 rounded flex-shrink-0"
                  style={{ backgroundColor: color }}
                  title={`Color for ${signer.label}`}
                />

                {/* Drag handle (for sequential) */}
                {routing === "sequential" && (
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === signers.length - 1}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Signing order badge (for sequential) */}
                {routing === "sequential" && (
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0"
                    style={{ backgroundColor: color }}
                  >
                    {index + 1}
                  </div>
                )}

                {/* Label input */}
                <div className="flex-1 min-w-0">
                  <Input
                    type="text"
                    value={signer.label}
                    onChange={(e) => handleLabelChange(signer.id, e.target.value)}
                    placeholder="Signer role name"
                    className="text-sm"
                  />
                  <div className="text-xs text-gray-400 mt-0.5">
                    Key: {signer.key}
                  </div>
                </div>

                {/* Required checkbox */}
                <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={signer.required}
                    onChange={(e) =>
                      handleRequiredChange(signer.id, e.target.checked)
                    }
                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Required
                  </span>
                </label>

                {/* Delete button */}
                <button
                  type="button"
                  onClick={() => handleRemoveSigner(signer.id)}
                  disabled={signers.length <= 1}
                  className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                  title={
                    signers.length <= 1
                      ? "At least one signer is required"
                      : "Remove signer"
                  }
                >
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Add signer button */}
        {signers.length < maxSigners && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddSigner}
            startIcon={<Plus size={16} />}
            className="mt-3"
          >
            Add Signer
          </Button>
        )}
      </div>

      {/* Color Legend */}
      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
          Color Legend (matches PDF signature boxes)
        </div>
        <div className="flex flex-wrap gap-2">
          {signers.map((signer, index) => {
            const color = SIGNER_COLORS[index % SIGNER_COLORS.length];
            return (
              <div
                key={signer.id}
                className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400"
              >
                <div
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: color }}
                />
                <span>{signer.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SignerRolesEditor;
